// Drag-to-reorder on the home board (HOME_WIDGETS_SPEC.md §6): in edit mode a
// widget is picked up, follows the pointer, and every time it crosses another
// widget it takes that widget's place in the ORDER — the CSS grid reflows the
// neighbours at once (iPad behaviour, no free placement, no holes). Dropping
// persists the order through the page's durable prefs write; a cancelled
// gesture restores the order it started from.
//
// Pointer events cover mouse, pen and touch alike (the frame sets
// `touch-action: none` in edit mode so a touch drag does not scroll). One drag
// at a time, so its bookkeeping is a plain handle on the root state
// (`root._homeDragging`, like `root._homeOps`) and one reactive key,
// `_homeDragId`, for the lifted look.
//
// `this` is the frame element (el.call). Hit-testing reads the grid's own
// child nodes and their rects (reading `el.node` is allowed; no document
// query), and the card follows the pointer through `setNodeStyles`, the
// element's sanctioned inline-style escape hatch.
//
// MOTION (DESIGN-WL-HOME-WIDTH-RESIZE-1, functions/homeWidgetMotion.js): the
// lifted card grows to `scale 1.02` (DESIGN_SPEC §6.4 "dragged card: lift
// shadow float + scale(1.02), A" — the individual `scale` property, which the
// frame transitions at `A`); the neighbours glide into their new cells (the
// board wraps `moveWidget` in its FLIP); the drop hands the pointer offset to
// the frame's `translate` transition, so the card SETTLES into its cell instead
// of jumping. The pointer-follow itself is the inline `transform`, which the
// frame never transitions, so the card stays under the finger. Reduced motion
// or a hidden tab: no lift and no glide (homeWidgetMotionAllowed).

export const homeWidgetDragStart = function homeWidgetDragStart(e, s) {
  const root = s && s.root
  if (!root || !root._homeCustomizeMode || !this || !this.node) return
  if (e && e.button !== undefined && e.button !== 0) return
  // Never from a control — but the title and the app tile are the card's own
  // buttons (they open it outside edit mode), so in edit mode they are handles.
  const t = e && e.target
  const control =
    t &&
    t.closest &&
    t.closest('button, a, input, textarea, select, [role="radio"], [role="tab"], .gs-no-drag')
  if (control && !(control.matches && control.matches('.widget-title, .widget-app-tile'))) return
  const rect = this.node.getBoundingClientRect()
  root._homeDragging = {
    id: s.id,
    pointerId: e.pointerId,
    startX: e.clientX,
    startY: e.clientY,
    grabX: e.clientX - rect.left,
    grabY: e.clientY - rect.top,
    dx: 0,
    dy: 0,
    moved: false,
    lastTarget: null,
    before: Array.isArray(root._homeLayout) ? root._homeLayout.slice() : null
  }
  try {
    this.node.setPointerCapture(e.pointerId)
  } catch (_) {
    /* synthetic events carry no capturable pointer */
  }
}

export const homeWidgetDragMove = function homeWidgetDragMove(e, s) {
  const root = s && s.root
  const d = root && root._homeDragging
  if (!d || d.id !== s.id || (d.pointerId !== undefined && e.pointerId !== d.pointerId)) return
  if (!d.moved) {
    // A small dead zone keeps a click a click.
    if (Math.hypot(e.clientX - d.startX, e.clientY - d.startY) < 6) return
    d.moved = true
    root.update({ _homeDragId: d.id }, { preventFetch: true })
    // The lift (DESIGN_SPEC §6.4) — only where motion is allowed.
    d.lifted = !!this.call('homeWidgetMotionAllowed', this.node)
    if (d.lifted) this.setNodeStyles({ scale: '1.02' })
  }
  if (e.cancelable) e.preventDefault()
  const node = this.node
  const gridNode = node && node.parentElement
  if (!gridNode) return
  let target = null
  for (const child of gridNode.children) {
    if (child === node) continue
    const id = child.getAttribute && child.getAttribute('data-widget-id')
    if (!id) continue
    const r = child.getBoundingClientRect()
    if (!r.width || !r.height) continue
    if (e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom) {
      target = id
      break
    }
  }
  // Follow the pointer from wherever the flow has put the card now: its rect
  // includes the current translate, so subtract it to get the resting place.
  const follow = (x, y) => {
    const r = node.getBoundingClientRect()
    const baseLeft = r.left - d.dx
    const baseTop = r.top - d.dy
    d.dx = x - d.grabX - baseLeft
    d.dy = y - d.grabY - baseTop
    this.setNodeStyles({ transform: `translate(${Math.round(d.dx)}px, ${Math.round(d.dy)}px)` })
  }
  if (target && target !== d.lastTarget && root._homeOps && root._homeOps.moveWidget) {
    d.lastTarget = target
    root._homeOps.moveWidget(d.id, target)
    // The new order reaches the DOM in smbls' effect flush, one microtask
    // later: re-anchor the card to its NEW cell then, before any frame paints,
    // so it never jumps by the cell it just left.
    const x = e.clientX
    const y = e.clientY
    const again = () => {
      if (root._homeDragging === d) follow(x, y)
    }
    if (typeof queueMicrotask === 'function') queueMicrotask(again)
    else Promise.resolve().then(again)
  }
  follow(e.clientX, e.clientY)
}

export const homeWidgetDragEnd = function homeWidgetDragEnd(e, s, cancelled) {
  const root = s && s.root
  const d = root && root._homeDragging
  if (!d || d.id !== s.id) return
  root._homeDragging = null
  try {
    if (this.node && d.pointerId !== undefined) this.node.releasePointerCapture(d.pointerId)
  } catch (_) {
    /* already released */
  }
  if (!d.moved) {
    this.setNodeStyles({ transform: '' })
    return
  }
  // SETTLE: the card sits in its cell already (the live reorder moved it
  // there); the pointer offset moves from the instant `transform` to the
  // transitioned `translate`, so clearing it glides the card home and the
  // lift eases off. Without motion the card simply lands.
  if (d.lifted && this.node) {
    this.setNodeStyles({
      transition: 'none',
      translate: `${Math.round(d.dx)}px ${Math.round(d.dy)}px`,
      transform: ''
    })
    void this.node.offsetWidth
    this.setNodeStyles({ transition: '', translate: '', scale: '' })
  } else {
    this.setNodeStyles({ transform: '', scale: '' })
  }
  root.update({ _homeDragId: null }, { preventFetch: true })
  const ops = root._homeOps
  if (!ops) return
  if (cancelled) {
    if (d.before && ops.setLayout) ops.setLayout(d.before, { persist: false })
    return
  }
  if (ops.persistLayout) ops.persistLayout()
}

// The keyboard path: in edit mode the arrow keys move the focused widget one
// place back or forward in the order, and the new order persists at once.
// Shift + an arrow is the RESIZE path (functions/shortcutsHome.js
// `homeWidgetKeyResize`) and ⌘ / Ctrl / Alt + an arrow belong to the browser,
// so a modified arrow is never taken here.
export const homeWidgetKeyMove = function homeWidgetKeyMove(e, s) {
  const root = s && s.root
  if (!e || e.shiftKey || e.metaKey || e.ctrlKey || e.altKey) return
  if (!root || !root._homeCustomizeMode || !root._homeOps || !root._homeOps.moveBy) return
  const delta =
    e.key === 'ArrowLeft' || e.key === 'ArrowUp'
      ? -1
      : e.key === 'ArrowRight' || e.key === 'ArrowDown'
        ? 1
        : 0
  if (!delta) return
  e.preventDefault()
  root._homeOps.moveBy(s.id, delta)
}
